import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, ChevronRight, BarChart3, Wallet, CreditCard, Target,
  TrendingUp, TrendingDown, DollarSign, PieChart, Activity, FileText,
  AlertTriangle, CheckCircle, Gem, Factory, Globe, Shield, Clock, XCircle,
  Users, Package, Building2, ShoppingCart, Percent, Landmark, Receipt,
  FolderOpen, Download, ChevronDown, FileBarChart, Calendar, Archive,
  History, RotateCcw, Plus, FileSpreadsheet, Briefcase, Calculator,
  FileCheck, Send, X, Sparkles, ArrowUpRight, ArrowDownRight, Minus,
  Settings, HelpCircle, Type
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { usePresentationMode } from '../context/PresentationModeContext';
import { mockData, externalAuditData } from '../data/mockData';
import { Button } from '../components/ui/button';
import PresentationModeButton from '../components/PresentationModeButton';
import ReportPreview from '../components/ReportPreview';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend, ComposedChart, Area, PieChart as RePieChart, Pie, Cell,
  AreaChart
} from 'recharts';
import RevenueWorldMap from '../components/RevenueWorldMap';
import FinanceFlowMap from '../components/FinanceFlowMap';
import COGSAnalysis from './finance/COGSAnalysis';
import SubcontractsAnalysis from './finance/SubcontractsAnalysis';
import CommercialCostsAnalysis from './finance/CommercialCostsAnalysis';
import GeneralAdminAnalysis from './finance/GeneralAdminAnalysis';
import FinancialPerformance from './finance/FinancialPerformance';
import DebtFinancing from './finance/DebtFinancing';
import CapexInvestment from './finance/CapexInvestment';
import LiquidityCashFlow from './finance/LiquidityCashFlow';
import BalanceSheet from './finance/BalanceSheet';
import RevenueAnalysis from './finance/RevenueAnalysis';
import EBITDADetail from './finance/EBITDADetail';
import OPEXDetail from './finance/OPEXDetail';
import PersonnelCosts from './finance/PersonnelCosts';
import NetIncomeDetail from './finance/NetIncomeDetail';
import NonOperatingResults from './finance/NonOperatingResults';
import FinancialResults from './finance/FinancialResults';
import IndustrialTax from './finance/IndustrialTax';
import CostPerCaratDetail from './finance/CostPerCaratDetail';
import SocialResponsibility from './finance/SocialResponsibility';
import ExternalAudit from './finance/ExternalAudit';
import ExecutiveSummary from './finance/ExecutiveSummary';
import FinancialExecutiveSummary from './finance/FinancialExecutiveSummary';
import IncomeStatementMain from './finance/IncomeStatementMain';
import FinanceMultiPeriodComparative from './finance/FinanceMultiPeriodComparative';
import { 
  DynamicConfigurationModal, 
  PlatformConfigurationModal, 
  GeneratingModal, 
  PreviewModal 
} from '../components/ReportConfigurationModal';

const COLORS = ['#0A192F', '#C5A059', '#059669', '#3B82F6', '#8B5CF6', '#EC4899'];

// Month translation mapping for Portuguese
const monthTranslations = {
  'Jan': { en: 'Jan', pt: 'Jan' },
  'Feb': { en: 'Feb', pt: 'Fev' },
  'Mar': { en: 'Mar', pt: 'Mar' },
  'Apr': { en: 'Apr', pt: 'Abr' },
  'May': { en: 'May', pt: 'Mai' },
  'Jun': { en: 'Jun', pt: 'Jun' },
  'Jul': { en: 'Jul', pt: 'Jul' },
  'Aug': { en: 'Aug', pt: 'Ago' },
  'Sep': { en: 'Sep', pt: 'Set' },
  'Oct': { en: 'Oct', pt: 'Out' },
  'Nov': { en: 'Nov', pt: 'Nov' },
  'Dec': { en: 'Dec', pt: 'Dez' }
};

const translateMonth = (month, language) => {
  return monthTranslations[month]?.[language] || month;
};

// ============ Dynamic Reports Section Component ============
// Premium executive reports with critical questions, executive notes, and preview
const DynamicReportsSection = ({ language, showChatbot, setShowChatbot, chatbotSession, setChatbotSession }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // State for the complete flow
  const [flowState, setFlowState] = useState({
    step: 'idle', // idle, validating, questions, generating, preview, success, error
    selectedReport: null,
    validationData: null,
    answers: {},
    executiveNotes: {}, // {metric_id: note_text}
    isFinalVersion: false,
    previewUrl: null,
    downloadUrl: null,
    showHtmlPreview: false,
    isDownloading: false,
    error: null
  });

  // Report archive data
  const reportArchiveData = {
    2025: [
      { name: language === 'pt' ? 'Relatório Anual 2025' : 'Annual Report 2025', date: '2025-12-31', type: 'annual', year: '2025' },
      { name: language === 'pt' ? 'Relatório Semestral H1 2025' : 'H1 2025 Report', date: '2025-06-30', type: 'semester-h1', year: '2025' },
    ],
    2024: [
      { name: language === 'pt' ? 'Relatório Anual 2024' : 'Annual Report 2024', date: '2024-12-31', type: 'annual', year: '2024' },
      { name: language === 'pt' ? 'Relatório Semestral H2 2024' : 'H2 2024 Report', date: '2024-12-31', type: 'semester-h2', year: '2024' },
      { name: language === 'pt' ? 'Relatório Semestral H1 2024' : 'H1 2024 Report', date: '2024-06-30', type: 'semester-h1', year: '2024' },
      { name: language === 'pt' ? 'Relatório de Auditoria 2024' : 'Audit Report 2024', date: '2024-03-15', type: 'audit', year: '2024' },
    ],
    2023: [
      { name: language === 'pt' ? 'Relatório Anual 2023' : 'Annual Report 2023', date: '2023-12-31', type: 'annual', year: '2023' },
      { name: language === 'pt' ? 'Relatório de Auditoria 2023' : 'Audit Report 2023', date: '2023-03-15', type: 'audit', year: '2023' },
    ],
  };

  const availableReportsData = [
    { 
      id: 'demonstracao_resultados', 
      name: { pt: 'Demonstração de Resultados', en: 'Income Statement' }, 
      icon: FileText, 
      description: { pt: 'Relatório executivo de P&L com dados em tempo real', en: 'Executive P&L report with real-time data' },
      available: false,
      inDevelopment: true,
      dynamicData: true
    },
    { 
      id: 'liquidez_fluxo_caixa', 
      name: { pt: 'Liquidez e Fluxo de Caixa', en: 'Liquidity & Cash Flow' }, 
      icon: Wallet, 
      description: { pt: 'Fluxos de caixa e posição de liquidez', en: 'Cash flows and liquidity position' }, 
      available: true, 
      inDevelopment: false,
      dynamicData: true
    },
    { id: 'balance-sheet-report', name: { pt: 'Balanço Patrimonial', en: 'Balance Sheet' }, icon: PieChart, description: { pt: 'Activos, passivos e capital próprio', en: 'Assets, liabilities and equity' }, available: false, inDevelopment: true },
    { 
      id: 'performance_financeira', 
      name: { pt: 'Performance Financeira', en: 'Financial Performance' }, 
      icon: TrendingUp, 
      description: { pt: 'Receita, EBITDA e margens', en: 'Revenue, EBITDA and margins' }, 
      available: true, 
      inDevelopment: false,
      dynamicData: true
    },
    { id: 'budget-execution', name: { pt: 'Execução Orçamental', en: 'Budget Execution' }, icon: Target, description: { pt: 'Comparativo orçado vs realizado', en: 'Budget vs actual comparison' }, available: false, inDevelopment: true },
    { id: 'debt-financing', name: { pt: 'Dívida e Financiamento', en: 'Debt & Financing' }, icon: CreditCard, description: { pt: 'Estrutura de dívida e covenants', en: 'Debt structure and covenants' }, available: false, inDevelopment: true },
    { id: 'capex-investment', name: { pt: 'CAPEX e Investimento', en: 'CAPEX & Investment' }, icon: Factory, description: { pt: 'Investimentos e projectos de capital', en: 'Capital investments and projects' }, available: false, inDevelopment: true },
  ];

  // Step 1: Start validation and get critical questions
  const startReportFlow = async (reportId) => {
    if (flowState.step !== 'idle') return;
    
    setFlowState(prev => ({ ...prev, step: 'validating', selectedReport: reportId, error: null }));
    
    try {
      const response = await fetch(`${API_URL}/api/executive-reports/validate/${reportId}?lang=${language}`);
      if (!response.ok) throw new Error('Validation failed');
      const data = await response.json();
      
      const defaultAnswers = {};
      if (data.critical_questions) {
        data.critical_questions.forEach(q => {
          defaultAnswers[q.id] = q.default;
        });
      }
      
      setFlowState(prev => ({ 
        ...prev, 
        step: 'questions',
        validationData: data,
        answers: defaultAnswers,
        executiveNotes: {},
        isFinalVersion: false
      }));
    } catch (error) {
      console.error('Validation error:', error);
      setFlowState(prev => ({ ...prev, step: 'error', error: language === 'pt' ? 'Erro ao validar dados' : 'Error validating data' }));
    }
  };

  // Step 2: Handle answer selection
  const selectAnswer = (questionId, value) => {
    setFlowState(prev => ({
      ...prev,
      answers: { ...prev.answers, [questionId]: value }
    }));
  };

  // Step 2b: Handle executive note input
  const updateExecutiveNote = (metricId, noteText) => {
    setFlowState(prev => ({
      ...prev,
      executiveNotes: { ...prev.executiveNotes, [metricId]: noteText }
    }));
  };

  // Step 2c: Toggle final version
  const toggleFinalVersion = () => {
    setFlowState(prev => ({ ...prev, isFinalVersion: !prev.isFinalVersion }));
  };

  // Step 3: Generate and preview report
  const generateAndPreview = async () => {
    setFlowState(prev => ({ ...prev, step: 'generating' }));
    
    try {
      const reportId = flowState.selectedReport;
      
      // Prepare request body
      const requestBody = {
        report_id: reportId,
        answers: Object.entries(flowState.answers).map(([id, value]) => ({ question_id: id, answer: value })),
        executive_notes: Object.entries(flowState.executiveNotes)
          .filter(([_, note]) => note.trim())
          .map(([id, note]) => ({ metric_id: id, note: note.trim() })),
        is_final_version: flowState.isFinalVersion,
        language: language  // Pass current UI language to generate PDF in correct language
      };
      
      // Generate report (this prepares the PDF for later download)
      const genResponse = await fetch(`${API_URL}/api/executive-reports/generate/${reportId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (!genResponse.ok) throw new Error('Generation failed');
      const genData = await genResponse.json();
      
      // Show HTML preview instead of opening PDF
      setFlowState(prev => ({ 
        ...prev, 
        step: 'preview', 
        downloadUrl: `${API_URL}${genData.download_url}`,
        showHtmlPreview: true
      }));
      
    } catch (error) {
      console.error('Generation error:', error);
      setFlowState(prev => ({ ...prev, step: 'error', error: language === 'pt' ? 'Erro ao gerar relatório' : 'Error generating report' }));
    }
  };

  // Step 4: Download final report
  const downloadReport = async () => {
    setFlowState(prev => ({ ...prev, isDownloading: true }));
    
    const reportId = flowState.selectedReport;
    const url = `${API_URL}/api/executive-reports/download/${reportId}`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `ABC_Demonstracao_Resultados_${new Date().toISOString().split('T')[0]}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      setFlowState(prev => ({ ...prev, step: 'success', isDownloading: false, showHtmlPreview: false }));
      setTimeout(resetFlow, 3000);
      
    } catch (error) {
      console.error('Download error:', error);
      setFlowState(prev => ({ ...prev, step: 'error', isDownloading: false, error: language === 'pt' ? 'Erro ao descarregar' : 'Download error' }));
    }
  };

  // Close HTML preview
  const closeHtmlPreview = () => {
    setFlowState(prev => ({ ...prev, showHtmlPreview: false, step: 'questions' }));
  };

  // Reset flow
  const resetFlow = () => {
    setFlowState({
      step: 'idle',
      selectedReport: null,
      validationData: null,
      answers: {},
      executiveNotes: {},
      isFinalVersion: false,
      previewUrl: null,
      downloadUrl: null,
      showHtmlPreview: false,
      isDownloading: false,
      error: null
    });
  };

  // Go back to questions from preview
  const backToQuestions = () => {
    setFlowState(prev => ({ ...prev, step: 'questions' }));
  };

  // Legacy download for archive
  const handleDownloadPDF = (reportType, year) => {
    const url = `${API_URL}/api/reports/download/${reportType}?year=${year}&language=${language}`;
    window.open(url, '_blank');
  };

  // Year Accordion Component
  const YearAccordionWithDownload = ({ year, reports, lang }) => {
    const [isOpen, setIsOpen] = useState(year === '2025');
    return (
      <div>
        <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors">
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-primary" />
            <span className="font-semibold text-primary">{year}</span>
            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{reports.length} {lang === 'pt' ? 'documentos' : 'documents'}</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div className="bg-slate-50 px-5 py-3 space-y-2">
            {reports.map((report, idx) => (
              <div key={idx} className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-slate-100">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">{report.name}</p>
                    <p className="text-xs text-slate-400">{report.date}</p>
                  </div>
                </div>
                <button onClick={() => handleDownloadPDF(report.type, report.year)} className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors">
                  <Download className="w-3.5 h-3.5" />PDF
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Modals are now extracted to ReportConfigurationModal.jsx to prevent re-mounting issues
  return (
    <div className="space-y-6">
      {/* HTML Report Preview Modal */}
      {flowState.showHtmlPreview && (
        <ReportPreview
          reportId={flowState.selectedReport}
          language={language}
          onClose={closeHtmlPreview}
          onDownload={downloadReport}
          isDownloading={flowState.isDownloading}
        />
      )}

      {/* Title */}
      <div>
        <h2 className="font-serif text-3xl font-bold text-primary">
          {language === 'pt' ? 'Relatórios Executivos' : 'Executive Reports'}
        </h2>
        <p className="text-slate-500 mt-2">
          {language === 'pt' 
            ? 'Geração de relatórios premium com dados em tempo real' 
            : 'Generate premium reports with real-time data'}
        </p>
      </div>

      {/* Error Toast */}
      {flowState.step === 'error' && (
        <div className="fixed top-4 right-4 z-50 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg flex items-center gap-3">
          <XCircle className="w-5 h-5 text-red-500" />
          <span className="text-sm text-red-700">{flowState.error}</span>
          <button onClick={resetFlow} className="ml-2 text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Success Toast */}
      {flowState.step === 'success' && (
        <div className="fixed top-4 right-4 z-50 bg-emerald-50 border border-emerald-200 rounded-lg p-4 shadow-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-500" />
          <span className="text-sm text-emerald-700">
            {language === 'pt' ? 'Download concluído com sucesso!' : 'Download completed successfully!'}
          </span>
        </div>
      )}

      {/* Modals - Using extracted components to prevent re-mounting issues */}
      <DynamicConfigurationModal
        language={language}
        flowState={flowState}
        selectAnswer={selectAnswer}
        toggleFinalVersion={toggleFinalVersion}
        generateAndPreview={generateAndPreview}
        resetFlow={resetFlow}
      />
      <PreviewModal
        language={language}
        flowState={flowState}
        backToQuestions={backToQuestions}
        downloadReport={downloadReport}
      />
      <GeneratingModal language={language} step={flowState.step} />

      {/* Two columns layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* 1. Arquivo de Relatórios */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] px-5 py-4">
            <div className="flex items-center gap-3">
              <Archive className="w-5 h-5 text-gold-400" />
              <div>
                <h3 className="font-serif text-lg font-bold text-white">
                  {language === 'pt' ? 'Arquivo de Relatórios' : 'Report Archive'}
                </h3>
                <p className="text-slate-400 text-xs mt-0.5">
                  {language === 'pt' ? 'Histórico de documentos' : 'Document history'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="divide-y divide-slate-100">
            {Object.entries(reportArchiveData).map(([year, reports]) => (
              <YearAccordionWithDownload key={year} year={year} reports={reports} lang={language} />
            ))}
          </div>
        </div>

        {/* 2. Relatórios Dinâmicos */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] px-5 py-4">
            <div className="flex items-center gap-3">
              <FileBarChart className="w-5 h-5 text-gold-400" />
              <div>
                <h3 className="font-serif text-lg font-bold text-white">
                  {language === 'pt' ? 'Relatórios Premium' : 'Premium Reports'}
                </h3>
                <p className="text-slate-400 text-xs mt-0.5">
                  {language === 'pt' ? 'Board-level com dados dinâmicos' : 'Board-level with dynamic data'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-4 space-y-2">
            {availableReportsData.map((report) => {
              const IconComponent = report.icon;
              const isProcessing = flowState.selectedReport === report.id && 
                ['validating', 'generating'].includes(flowState.step);
              
              return (
                <div
                  key={report.id}
                  data-testid={`report-item-${report.id}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    report.available && !isProcessing
                      ? 'border-slate-100 hover:border-primary/30 hover:bg-primary/5 cursor-pointer' 
                      : report.available && isProcessing
                      ? 'border-primary/30 bg-primary/5'
                      : 'border-slate-100 bg-slate-50/50 cursor-not-allowed opacity-70'
                  }`}
                  onClick={() => report.available && !isProcessing && startReportFlow(report.id)}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    report.available ? 'bg-primary/10' : 'bg-slate-200'
                  }`}>
                    {isProcessing ? (
                      <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    ) : (
                      <IconComponent className={`w-4 h-4 ${report.available ? 'text-primary' : 'text-slate-400'}`} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-semibold ${report.available ? 'text-slate-700' : 'text-slate-500'}`}>
                        {report.name[language]}
                      </p>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{report.description[language]}</p>
                    {/* Pilot version notice for unavailable reports */}
                    {!report.available && (
                      <p className="text-[10px] text-slate-400 italic mt-1.5 leading-tight">
                        {language === 'pt' 
                          ? 'Disponível apenas na versão final da solução, não incluído nesta versão piloto.'
                          : 'Available only in the final version, not included in this pilot version.'}
                      </p>
                    )}
                  </div>
                  {report.available && (
                    <ChevronRight className="w-4 h-4 text-primary flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
          
          <div className="px-4 pb-4">
            <button 
              onClick={() => setShowChatbot(true)}
              className="w-full py-2.5 bg-gradient-to-r from-primary to-primary/80 text-white rounded-lg text-sm font-medium hover:from-primary/90 hover:to-primary/70 transition-all flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              {language === 'pt' ? 'Relatório Personalizado com IA' : 'Custom AI Report'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
const PlatformReportsSection = ({ language, showChatbot, setShowChatbot, chatbotSession, setChatbotSession }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const platformReportsData = [
    { 
      id: 'executive-summary-report', 
      name: { pt: 'Mensagens-Chave', en: 'Key Messages' }, 
      icon: FileText, 
      description: { pt: 'Visão geral executiva da empresa', en: 'Executive overview of the company' }
    },
    { 
      id: 'income-statement-report', 
      name: { pt: 'Demonstração de Resultados', en: 'Income Statement' }, 
      icon: FileBarChart, 
      description: { pt: 'P&L completo com análise vertical e horizontal', en: 'Complete P&L with vertical and horizontal analysis' }
    },
    { 
      id: 'liquidity-report', 
      name: { pt: 'Liquidez e Geração de Caixa', en: 'Liquidity & Cash Generation' }, 
      icon: Wallet, 
      description: { pt: 'Fluxos de caixa e posição de liquidez', en: 'Cash flows and liquidity position' }
    },
    { 
      id: 'debt-report', 
      name: { pt: 'Dívida e Financiamento', en: 'Debt & Financing' }, 
      icon: CreditCard, 
      description: { pt: 'Estrutura de dívida e covenants', en: 'Debt structure and covenants' }
    },
    { 
      id: 'capex-report', 
      name: { pt: 'CAPEX', en: 'CAPEX' }, 
      icon: Factory, 
      description: { pt: 'Investimentos e projectos de capital', en: 'Capital investments and projects' }
    },
    { 
      id: 'balance-sheet-report', 
      name: { pt: 'Balanço Patrimonial', en: 'Balance Sheet' }, 
      icon: PieChart, 
      description: { pt: 'Activos, passivos e capital próprio', en: 'Assets, liabilities and equity' }
    },
  ];

  // Chatbot Component
  const ReportGeneratorChatbot = ({ isOpen, onClose }) => {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [suggestions, setSuggestions] = useState([]);
    const [sessionId] = useState(() => `platform-report-${Date.now()}`);
    const messagesEndRef = React.useRef(null);

    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
      scrollToBottom();
    }, [messages]);

    useEffect(() => {
      if (isOpen && messages.length === 0) {
        initChat();
      }
    }, [isOpen]);

    const initChat = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/reports/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: '', session_id: sessionId, language, step: 0 })
        });
        const data = await response.json();
        setMessages([{ role: 'assistant', content: data.response }]);
        setSuggestions(data.suggestions || []);
        setCurrentStep(data.step);
      } catch (error) {
        console.error('Chat init error:', error);
        setMessages([{ role: 'assistant', content: language === 'pt' 
          ? 'Olá! Vou ajudá-lo a criar um relatório personalizado. Que tipo de análise gostaria de incluir?' 
          : 'Hello! I will help you create a custom report. What type of analysis would you like to include?' }]);
      }
      setIsLoading(false);
    };

    const sendMessage = async (message) => {
      if (!message.trim()) return;
      const userMessage = { role: 'user', content: message };
      setMessages(prev => [...prev, userMessage]);
      setInputValue('');
      setSuggestions([]);
      setIsLoading(true);

      try {
        const response = await fetch(`${API_URL}/api/reports/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message, session_id: sessionId, language, step: currentStep + 1 })
        });
        const data = await response.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
        setSuggestions(data.suggestions || []);
        setCurrentStep(data.step);
      } catch (error) {
        console.error('Chat error:', error);
        setMessages(prev => [...prev, { role: 'assistant', content: language === 'pt' 
          ? 'Desculpe, ocorreu um erro. Por favor tente novamente.' 
          : 'Sorry, an error occurred. Please try again.' }]);
      }
      setIsLoading(false);
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl w-[500px] max-h-[600px] flex flex-col shadow-2xl">
          <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] px-6 py-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gold-400/20 rounded-full flex items-center justify-center">
                <FileBarChart className="w-5 h-5 text-gold-400" />
              </div>
              <div>
                <h3 className="font-serif font-bold text-white">
                  {language === 'pt' ? 'Relatório Personalizado' : 'Custom Report'}
                </h3>
                <p className="text-xs text-slate-400">
                  {language === 'pt' ? 'Assistente de geração' : 'Generation assistant'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-lg px-4 py-2 ${
                  msg.role === 'user' ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-lg px-4 py-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {suggestions.length > 0 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {suggestions.map((sug, idx) => (
                <button
                  key={idx}
                  onClick={() => sendMessage(sug)}
                  className="text-xs bg-slate-100 text-slate-600 px-3 py-1.5 rounded-full hover:bg-slate-200"
                >
                  {sug}
                </button>
              ))}
            </div>
          )}

          <div className="p-4 border-t border-slate-100">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage(inputValue)}
                placeholder={language === 'pt' ? 'Escreva aqui...' : 'Type here...'}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <button
                onClick={() => sendMessage(inputValue)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Reuse the availableReportsData from DynamicReportsSection for Premium Reports
  const availableReportsData = [
    { 
      id: 'demonstracao_resultados', 
      name: { pt: 'Demonstração de Resultados', en: 'Income Statement' },
      description: { pt: 'Relatório executivo de P&L com dados em tempo real', en: 'Executive P&L report with real-time data' },
      icon: FileBarChart,
      available: false,
      format: 'PDF Premium'
    },
    { 
      id: 'liquidez_fluxo_caixa', 
      name: { pt: 'Liquidez e Fluxo de Caixa', en: 'Liquidity & Cash Flow' },
      description: { pt: 'Fluxos de caixa e posição de liquidez', en: 'Cash flows and liquidity position' },
      icon: Wallet,
      available: true,
      format: 'PDF Premium'
    },
    { 
      id: 'balanco_patrimonial', 
      name: { pt: 'Balanço Patrimonial', en: 'Balance Sheet' },
      description: { pt: 'Activos, passivos e capital próprio', en: 'Assets, liabilities and equity' },
      icon: PieChart,
      available: false,
      pages: 2,
      format: 'PDF'
    },
    { 
      id: 'performance_financeira', 
      name: { pt: 'Performance Financeira', en: 'Financial Performance' },
      description: { pt: 'Receita, EBITDA e margens', en: 'Revenue, EBITDA and margins' },
      icon: TrendingUp,
      available: true,
      format: 'PDF Premium'
    },
    { 
      id: 'execucao_orcamental', 
      name: { pt: 'Execução Orçamental', en: 'Budget Execution' },
      description: { pt: 'Comparativo orçado vs realizado', en: 'Budget vs actual comparison' },
      icon: Target,
      available: false,
      pages: 2,
      format: 'PDF'
    },
    { 
      id: 'divida_financiamento', 
      name: { pt: 'Dívida e Financiamento', en: 'Debt & Financing' },
      description: { pt: 'Estrutura de dívida e covenants', en: 'Debt structure and covenants' },
      icon: CreditCard,
      available: false,
      pages: 2,
      format: 'PDF'
    },
    { 
      id: 'capex_investimento', 
      name: { pt: 'CAPEX e Investimento', en: 'CAPEX & Investment' },
      description: { pt: 'Investimentos e projectos de capital', en: 'Capital investments and projects' },
      icon: Factory,
      available: false,
      pages: 2,
      format: 'PDF'
    },
  ];

  // State for Premium Report Modal
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportConfig, setReportConfig] = useState({
    criticalQuestions: [],
    answers: {},
    executiveNotes: {},
    triggeredNotes: [],
    isFinalVersion: false,
    reportFormat: 'compact_10', // 'compact_10' (10 slides) or 'expanded_20' (20 slides - pilot disabled)
    selectedPeriod: '1T26', // Default to 1T26 (only available in pilot)
    customLabels: {
      kpiSectionTitle: ''  // Empty = use default translation based on language
    }
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedReportUrl, setGeneratedReportUrl] = useState(null);
  const [configStep, setConfigStep] = useState('questions'); // 'questions' | 'generating' | 'preview'
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Fetch critical questions and material variances when report is selected
  const handleReportSelect = async (reportId) => {
    if (!availableReportsData.find(r => r.id === reportId)?.available) return;
    
    setSelectedReport(reportId);
    setConfigStep('questions');
    setReportConfig({
      criticalQuestions: [],
      answers: {},
      executiveNotes: {},
      triggeredNotes: [],
      isFinalVersion: false,
      reportFormat: 'compact_10',
      selectedPeriod: '1T26',
      customLabels: {
        kpiSectionTitle: ''  // Empty = use default translation based on language
      }
    });

    try {
      const response = await fetch(`${API_URL}/api/executive-reports/validate/${reportId}?lang=${language}`);
      const data = await response.json();
      
      if (data.is_valid) {
        setReportConfig(prev => ({
          ...prev,
          criticalQuestions: data.critical_questions || [],
          triggeredNotes: data.material_variances || [],
          answers: (data.critical_questions || []).reduce((acc, q) => {
            // Find the first non-disabled option, or use default
            const enabledOption = q.options.find(opt => !opt.disabled);
            acc[q.id] = enabledOption?.value || q.default || q.options[0]?.value || '';
            return acc;
          }, {}),
        }));
      }
    } catch (error) {
      console.error('Error fetching report config:', error);
    }
  };

  // Generate the PDF report
  const handleGenerateReport = async () => {
    setConfigStep('generating');
    setIsGenerating(true);

    try {
      const answersArray = Object.entries(reportConfig.answers).map(([id, answer]) => ({
        question_id: id,
        answer: answer
      }));

      const notesArray = Object.entries(reportConfig.executiveNotes)
        .filter(([_, note]) => note && note.trim())
        .map(([id, note]) => ({
          metric_id: id,
          note: note
        }));

      const response = await fetch(`${API_URL}/api/executive-reports/generate/${selectedReport}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_id: selectedReport,
          answers: answersArray,
          executive_notes: notesArray,
          is_final_version: false, // Always DRAFT in pilot version
          report_format: reportConfig.reportFormat,
          custom_labels: {
            kpi_section_title: reportConfig.customLabels.kpiSectionTitle
          },
          language: language  // Pass current language to backend
        })
      });

      const data = await response.json();
      
      if (data.status === 'generated' && data.download_url) {
        setGeneratedReportUrl(data.download_url);
        // Show HTML preview instead of opening PDF
        setShowHtmlPreview(true);
        setConfigStep('preview');
      }
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Download the generated report
  const handleDownloadReport = () => {
    if (selectedReport) {
      const downloadUrl = `${API_URL}/api/executive-reports/download/${selectedReport}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `ABC_${selectedReport}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Close modal after download
      setSelectedReport(null);
      setGeneratedReportUrl(null);
      setConfigStep('questions');
      setShowHtmlPreview(false);
    }
  };

  // Download PDF from HTML preview
  const handleDownloadFromPreview = async () => {
    if (!selectedReport) return;
    
    setIsDownloadingPdf(true);
    const downloadUrl = `${API_URL}/api/executive-reports/download/${selectedReport}`;
    
    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ABC_${selectedReport}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Close everything after successful download
      setShowHtmlPreview(false);
      setSelectedReport(null);
      setGeneratedReportUrl(null);
      setConfigStep('questions');
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Close HTML preview
  const handleCloseHtmlPreview = () => {
    setShowHtmlPreview(false);
    setConfigStep('questions');
  };

  // ConfigurationModal is now extracted to ReportConfigurationModal.jsx to prevent re-mounting issues

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-3xl font-bold text-primary">
          {language === 'pt' ? 'Relatórios Gerados pela Plataforma' : 'Platform Generated Reports'}
        </h2>
        <p className="text-slate-500 mt-2">
          {language === 'pt' 
            ? 'Relatórios executivos premium gerados com dados em tempo real' 
            : 'Premium executive reports generated with real-time data'}
        </p>
      </div>

      {/* Premium Reports Section */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] px-5 py-4">
          <div className="flex items-center gap-3">
            <Gem className="w-5 h-5 text-gold-400" />
            <div>
              <h3 className="font-serif text-lg font-bold text-white">
                {language === 'pt' ? 'Relatórios Premium' : 'Premium Reports'}
              </h3>
              <p className="text-slate-400 text-xs mt-0.5">
                {language === 'pt' ? 'Board-level com dados dinâmicos' : 'Board-level with dynamic data'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-2">
          {availableReportsData.map((report) => {
            const IconComponent = report.icon;
            return (
              <div
                key={report.id}
                data-testid={`report-item-${report.id}`}
                onClick={() => report.available && handleReportSelect(report.id)}
                className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                  report.available 
                    ? 'border-slate-200 hover:border-primary/30 hover:bg-primary/5 cursor-pointer' 
                    : 'border-slate-100 bg-slate-50/50 opacity-70 cursor-not-allowed'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  report.available ? 'bg-primary/10' : 'bg-slate-100'
                }`}>
                  <IconComponent className={`w-5 h-5 ${report.available ? 'text-primary' : 'text-slate-400'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-semibold ${report.available ? 'text-slate-700' : 'text-slate-500'}`}>
                      {report.name[language]}
                    </p>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{report.description[language]}</p>
                  {/* Pilot version notice for unavailable reports */}
                  {!report.available && (
                    <p className="text-[10px] text-slate-400 italic mt-1.5 leading-tight">
                      {language === 'pt' 
                        ? 'Disponível apenas na versão final da solução, não incluído nesta versão piloto.'
                        : 'Available only in the final version, not included in this pilot version.'}
                    </p>
                  )}
                </div>
                {report.available && (
                  <ChevronRight className="w-5 h-5 text-primary flex-shrink-0" />
                )}
              </div>
            );
          })}
        </div>

        {/* Custom Report Button */}
        <div className="px-4 pb-4">
          <button 
            onClick={() => setShowChatbot(true)}
            className="w-full py-3 bg-gradient-to-r from-[#0A192F] to-[#1a2744] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors flex items-center justify-center gap-2"
          >
            <Sparkles className="w-4 h-4 text-gold-400" />
            {language === 'pt' ? 'Relatório Personalizado com IA' : 'Custom Report with AI'}
          </button>
        </div>
      </div>

      {/* Configuration Modal - Using extracted component to prevent re-mounting issues */}
      <PlatformConfigurationModal
        language={language}
        selectedReport={selectedReport}
        setSelectedReport={setSelectedReport}
        reportConfig={reportConfig}
        setReportConfig={setReportConfig}
        configStep={configStep}
        setConfigStep={setConfigStep}
        isGenerating={isGenerating}
        setIsGenerating={setIsGenerating}
        handleGenerateReport={handleGenerateReport}
        handleDownloadReport={handleDownloadReport}
        availableReportsData={availableReportsData}
        showHtmlPreview={showHtmlPreview}
        ReportPreviewComponent={ReportPreview}
        handleCloseHtmlPreview={handleCloseHtmlPreview}
        handleDownloadFromPreview={handleDownloadFromPreview}
        isDownloadingPdf={isDownloadingPdf}
      />

      {/* Chatbot Modal */}
      <ReportGeneratorChatbot 
        isOpen={showChatbot} 
        onClose={() => setShowChatbot(false)} 
      />
    </div>
  );
};

// Format millions with at least one decimal place
const formatM = (value) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return num.toFixed(1);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-primary text-white rounded-lg px-4 py-3 shadow-xl">
        <p className="text-sm font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color === '#0A192F' ? '#C5A059' : entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// McKinsey-style sidebar navigation
const SidebarNav = ({ activeSection, setActiveSection, language }) => {
  const [isPerformanceExpanded, setIsPerformanceExpanded] = React.useState(false);
  const [isBudgetExecutionExpanded, setIsBudgetExecutionExpanded] = React.useState(false);
  const [isOpexExpanded, setIsOpexExpanded] = React.useState(false);
  const [isOpex2026Expanded, setIsOpex2026Expanded] = React.useState(false);
  const [isIncomeStatementExpanded, setIsIncomeStatementExpanded] = React.useState(false);
  const [isIncomeStatement2026Expanded, setIsIncomeStatement2026Expanded] = React.useState(false);
  
  const mainSections = [
    { id: 'budget-execution', label: { en: 'Budget Execution 2026', pt: 'Execução Orçamental 2026' }, icon: Target, hasSubsections: true },
    { id: 'performance', label: { en: 'Financial Review 2025', pt: 'Revisão Financeira 2025' }, icon: BarChart3, hasSubsections: true },
    { id: 'multi-period-comparative', label: { en: 'Multi-Period Comparison', pt: 'Comparativo Multi-Período' }, icon: Calendar },
    { id: 'reports', label: { en: 'Reports & Institutional Docs', pt: 'Relatórios e Documentação Institucional' }, icon: FileBarChart, hasReportsSubsections: true },
  ];

  // Subsecções para Revisão Financeira 2025
  const performanceSubsections = [
    { id: 'income-statement', label: { en: 'Income Statement', pt: 'Demonstração de Resultados' }, icon: FileText, hasIncomeStatementSubsections: true },
    { id: 'liquidity', label: { en: 'Liquidity & Cash Generation', pt: 'Liquidez e Geração de Caixa' }, icon: Wallet },
    { id: 'debt', label: { en: 'Debt & Financing', pt: 'Dívida e Financiamento' }, icon: CreditCard },
    { id: 'capex', label: { en: 'CAPEX', pt: 'CAPEX' }, icon: Target },
    { id: 'balance-sheet', label: { en: 'Balance Sheet', pt: 'Balanço Patrimonial' }, icon: PieChart },
  ];

  // Subsecções para Execução Orçamental 2026 (espelho de 2025)
  const budgetExecutionSubsections = [
    { id: 'income-statement-2026', label: { en: 'Income Statement', pt: 'Demonstração de Resultados' }, icon: FileText, hasIncomeStatementSubsections: true },
    { id: 'liquidity-2026', label: { en: 'Liquidity & Cash Generation', pt: 'Liquidez e Geração de Caixa' }, icon: Wallet },
    { id: 'debt-2026', label: { en: 'Debt & Financing', pt: 'Dívida e Financiamento' }, icon: CreditCard },
    { id: 'capex-2026', label: { en: 'CAPEX', pt: 'CAPEX' }, icon: Target },
    { id: 'balance-sheet-2026', label: { en: 'Balance Sheet', pt: 'Balanço Patrimonial' }, icon: PieChart },
  ];

  // Subsecções dentro de Demonstração de Resultados (2025)
  const incomeStatementSubsections = [
    { id: 'revenue-detail', label: { en: 'Revenue', pt: 'Receitas' }, icon: DollarSign },
    { id: 'opex-detail', label: { en: 'Operating Costs', pt: 'Custos Operacionais' }, icon: Activity, hasOpexSubsections: true },
    { id: 'ebitda-detail', label: { en: 'EBITDA', pt: 'EBITDA' }, icon: TrendingUp },
    { id: 'non-operating', label: { en: 'Non-Operating Results', pt: 'Resultados Não Operacionais' }, icon: AlertTriangle },
    { id: 'financial-results', label: { en: 'Financial Results', pt: 'Resultados Financeiros' }, icon: Landmark },
    { id: 'industrial-tax', label: { en: 'Industrial Tax', pt: 'Imposto Industrial' }, icon: Receipt },
    { id: 'netincome-detail', label: { en: 'Net Income', pt: 'Resultado Líquido' }, icon: Target },
    { id: 'costpercarat-detail', label: { en: 'Unit Cost per Carat', pt: 'Custo Unitário por Quilate' }, icon: Gem },
  ];

  // Subsecções dentro de Demonstração de Resultados (2026)
  const incomeStatement2026Subsections = [
    { id: 'revenue-detail-2026', label: { en: 'Revenue', pt: 'Receitas' }, icon: DollarSign },
    { id: 'opex-detail-2026', label: { en: 'Operating Costs', pt: 'Custos Operacionais' }, icon: Activity, hasOpexSubsections: true },
    { id: 'ebitda-detail-2026', label: { en: 'EBITDA', pt: 'EBITDA' }, icon: TrendingUp },
    { id: 'non-operating-2026', label: { en: 'Non-Operating Results', pt: 'Resultados Não Operacionais' }, icon: AlertTriangle },
    { id: 'financial-results-2026', label: { en: 'Financial Results', pt: 'Resultados Financeiros' }, icon: Landmark },
    { id: 'industrial-tax-2026', label: { en: 'Industrial Tax', pt: 'Imposto Industrial' }, icon: Receipt },
    { id: 'netincome-detail-2026', label: { en: 'Net Income', pt: 'Resultado Líquido' }, icon: Target },
    { id: 'costpercarat-detail-2026', label: { en: 'Unit Cost per Carat', pt: 'Custo Unitário por Quilate' }, icon: Gem },
  ];

  const opexSubsections = [
    { id: 'opex-cogs', label: { en: 'COGS', pt: 'CMVMC' }, icon: Package },
    { id: 'opex-personnel', label: { en: 'Personnel Costs', pt: 'Custos com Pessoal' }, icon: Users },
    { id: 'opex-subcontracts', label: { en: 'Subcontracts', pt: 'Subcontratos' }, icon: FileText },
    { id: 'opex-fse', label: { en: 'General & Administrative', pt: 'Gerais e Administrativos' }, icon: Building2 },
    { id: 'opex-commercial', label: { en: 'Commercial Costs', pt: 'Custos de Comercialização' }, icon: ShoppingCart },
  ];

  const opex2026Subsections = [
    { id: 'opex-cogs-2026', label: { en: 'COGS', pt: 'CMVMC' }, icon: Package },
    { id: 'opex-personnel-2026', label: { en: 'Personnel Costs', pt: 'Custos com Pessoal' }, icon: Users },
    { id: 'opex-subcontracts-2026', label: { en: 'Subcontracts', pt: 'Subcontratos' }, icon: FileText },
    { id: 'opex-fse-2026', label: { en: 'General & Administrative', pt: 'Gerais e Administrativos' }, icon: Building2 },
    { id: 'opex-commercial-2026', label: { en: 'Commercial Costs', pt: 'Custos de Comercialização' }, icon: ShoppingCart },
  ];

  const reportsSubsections = [
    { id: 'external-reports', label: { en: 'Auditor Report', pt: 'Relatório do Auditor' }, icon: FileCheck },
    { id: 'internal-docs', label: { en: 'Financial Reports', pt: 'Relatórios Financeiros' }, icon: FolderOpen },
    { id: 'platform-reports', label: { en: 'Platform Generated Reports', pt: 'Relatórios Gerados pela Plataforma' }, icon: FileBarChart },
  ];

  const isPerformanceSection = (id) => {
    return id === 'performance' || performanceSubsections.some(sub => sub.id === id) || incomeStatementSubsections.some(sub => sub.id === id) || opexSubsections.some(sub => sub.id === id);
  };

  const isBudgetExecutionSection = (id) => {
    return id === 'budget-execution' || budgetExecutionSubsections.some(sub => sub.id === id) || incomeStatement2026Subsections.some(sub => sub.id === id) || opex2026Subsections.some(sub => sub.id === id);
  };

  const isIncomeStatementSection = (id) => {
    return id === 'income-statement' || incomeStatementSubsections.some(sub => sub.id === id) || opexSubsections.some(sub => sub.id === id);
  };

  const isIncomeStatement2026Section = (id) => {
    return id === 'income-statement-2026' || incomeStatement2026Subsections.some(sub => sub.id === id) || opex2026Subsections.some(sub => sub.id === id);
  };

  const isOpexSection = (id) => {
    return id === 'opex-detail' || opexSubsections.some(sub => sub.id === id);
  };

  const isOpex2026Section = (id) => {
    return id === 'opex-detail-2026' || opex2026Subsections.some(sub => sub.id === id);
  };

  const isReportsSection = (id) => {
    return id === 'reports' || reportsSubsections.some(sub => sub.id === id);
  };

  // State for reports expansion
  const [isReportsExpanded, setIsReportsExpanded] = useState(false);

  // Auto-expand only when navigating to a specific subsection (not the main section)
  React.useEffect(() => {
    // 2025 sections - only expand when in a subsection, not the main 'performance' section
    if (performanceSubsections.some(sub => sub.id === activeSection) || incomeStatementSubsections.some(sub => sub.id === activeSection) || opexSubsections.some(sub => sub.id === activeSection)) {
      setIsPerformanceExpanded(true);
    }
    if (incomeStatementSubsections.some(sub => sub.id === activeSection) || opexSubsections.some(sub => sub.id === activeSection)) {
      setIsIncomeStatementExpanded(true);
    }
    if (opexSubsections.some(sub => sub.id === activeSection)) {
      setIsOpexExpanded(true);
    }
    // 2026 sections - only expand when in a subsection, not the main 'budget-execution' section
    if (budgetExecutionSubsections.some(sub => sub.id === activeSection) || incomeStatement2026Subsections.some(sub => sub.id === activeSection) || opex2026Subsections.some(sub => sub.id === activeSection)) {
      setIsBudgetExecutionExpanded(true);
    }
    if (incomeStatement2026Subsections.some(sub => sub.id === activeSection) || opex2026Subsections.some(sub => sub.id === activeSection)) {
      setIsIncomeStatement2026Expanded(true);
    }
    if (opex2026Subsections.some(sub => sub.id === activeSection)) {
      setIsOpex2026Expanded(true);
    }
    // Reports
    if (reportsSubsections.some(sub => sub.id === activeSection)) {
      setIsReportsExpanded(true);
    }
  }, [activeSection]);

  const handlePerformanceClick = () => {
    if (activeSection === 'performance') {
      setIsPerformanceExpanded(!isPerformanceExpanded);
    } else {
      setActiveSection('performance');
      setIsPerformanceExpanded(true);
    }
  };

  const handleIncomeStatementClick = () => {
    // Se já estamos exactamente na página income-statement, apenas alternar expansão
    if (activeSection === 'income-statement') {
      setIsIncomeStatementExpanded(!isIncomeStatementExpanded);
    } else {
      // Se estamos numa sub-secção (CMVMC, etc.) ou qualquer outra página, navegar para income-statement
      setActiveSection('income-statement');
      setIsIncomeStatementExpanded(true);
    }
  };

  return (
    <div className="w-72 bg-gradient-to-br from-[#0A192F] via-[#112240] to-[#0A192F] flex flex-col h-screen sticky top-0 shadow-2xl">
      {/* Logo/Title - Following Operations pattern */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center shadow-lg">
            <DollarSign className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-white font-serif text-lg font-bold tracking-wide">
              {language === 'pt' ? 'Finanças' : 'Finance'}
            </h1>
            <p className="text-white/50 text-xs">{language === 'pt' ? 'Empresa ABC' : 'Company ABC'}</p>
          </div>
        </div>
      </div>

      {/* Navigation Items */}
      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {mainSections.map((section) => {
          const Icon = section.icon;
          const isActive = activeSection === section.id || 
            (section.id === 'performance' && isPerformanceSection(activeSection)) ||
            (section.id === 'budget-execution' && isBudgetExecutionSection(activeSection)) ||
            (section.id === 'reports' && reportsSubsections.some(sub => sub.id === activeSection));
          
          const handleSectionClick = () => {
            if (section.id === 'performance') {
              // Apenas navegar para 'performance', não expandir
              setActiveSection('performance');
            } else if (section.id === 'budget-execution') {
              // Apenas navegar para 'budget-execution', não expandir
              setActiveSection('budget-execution');
            } else if (section.hasReportsSubsections) {
              // Apenas navegar para 'reports', não expandir
              setActiveSection('external-reports');
            } else {
              setActiveSection(section.id);
            }
          };

          return (
            <React.Fragment key={section.id}>
              <div
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 group ${
                  isActive 
                    ? 'bg-white/10 text-white' 
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`nav-${section.id}`}
              >
                <button
                  onClick={handleSectionClick}
                  className="flex items-center gap-3 flex-1"
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-gold-400' : ''}`} />
                  <span className="font-medium text-sm text-left">
                    {section.label[language]}
                  </span>
                </button>
                {section.hasSubsections && section.id === 'performance' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsPerformanceExpanded(!isPerformanceExpanded);
                    }}
                    className="p-1 hover:bg-white/10 rounded transition-colors"
                    data-testid={`toggle-${section.id}`}
                  >
                    <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isPerformanceExpanded ? 'rotate-90' : ''} ${isActive ? 'text-gold-400' : ''}`} />
                  </button>
                )}
                {section.hasSubsections && section.id === 'budget-execution' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsBudgetExecutionExpanded(!isBudgetExecutionExpanded);
                    }}
                    className="p-1 hover:bg-white/10 rounded transition-colors"
                    data-testid={`toggle-${section.id}`}
                  >
                    <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isBudgetExecutionExpanded ? 'rotate-90' : ''} ${isActive ? 'text-gold-400' : ''}`} />
                  </button>
                )}
                {section.hasReportsSubsections && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsReportsExpanded(!isReportsExpanded);
                    }}
                    className="p-1 hover:bg-white/10 rounded transition-colors"
                    data-testid={`toggle-${section.id}`}
                  >
                    <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isReportsExpanded ? 'rotate-90' : ''} ${isActive ? 'text-gold-400' : ''}`} />
                  </button>
                )}
              </div>
              
              {/* Performance Subsections (2025) */}
              {section.id === 'performance' && isPerformanceExpanded && (
                <div className="ml-4 space-y-1 overflow-hidden transition-all duration-200">
                  {performanceSubsections.map((sub) => {
                    const SubIcon = sub.icon;
                    const isSubActive = activeSection === sub.id || (sub.hasIncomeStatementSubsections && isIncomeStatementSection(activeSection));
                    
                    return (
                      <React.Fragment key={sub.id}>
                        <button
                          onClick={() => {
                            if (sub.hasIncomeStatementSubsections) {
                              handleIncomeStatementClick();
                            } else {
                              setActiveSection(sub.id);
                            }
                          }}
                          className={`w-[calc(100%-0.5rem)] flex items-center justify-between px-4 py-2.5 rounded-lg transition-all duration-200 ${
                            isSubActive 
                              ? 'bg-white/10 text-white' 
                              : 'text-white/60 hover:bg-white/5 hover:text-white'
                          }`}
                          data-testid={`nav-${sub.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <SubIcon className={`w-4 h-4 flex-shrink-0 ${isSubActive ? 'text-gold-400' : ''}`} />
                            <span className="text-xs font-medium text-left">
                              {sub.label[language]}
                            </span>
                          </div>
                          {sub.hasIncomeStatementSubsections && (
                            <ChevronRight className={`w-3 h-3 transition-transform ${isIncomeStatementExpanded ? 'rotate-90' : ''} ${isSubActive ? 'text-gold-400' : ''}`} />
                          )}
                        </button>
                        
                        {/* Income Statement Subsections (Receitas, EBITDA, Custos Operacionais, etc.) */}
                        {sub.hasIncomeStatementSubsections && isIncomeStatementExpanded && (
                          <div className="ml-4 space-y-1 overflow-hidden transition-all duration-200">
                            {incomeStatementSubsections.map((incomeSub) => {
                              const IncomeIcon = incomeSub.icon;
                              const isIncomeSubActive = activeSection === incomeSub.id || (incomeSub.hasOpexSubsections && opexSubsections.some(opexSub => opexSub.id === activeSection));
                              
                              const handleIncomeSubClick = () => {
                                if (incomeSub.hasOpexSubsections) {
                                  if (activeSection === incomeSub.id) {
                                    setIsOpexExpanded(!isOpexExpanded);
                                  } else if (isOpexSection(activeSection)) {
                                    setActiveSection(incomeSub.id);
                                    setIsOpexExpanded(true);
                                  } else {
                                    setActiveSection(incomeSub.id);
                                    setIsOpexExpanded(true);
                                  }
                                } else {
                                  setActiveSection(incomeSub.id);
                                }
                              };

                              return (
                                <React.Fragment key={incomeSub.id}>
                                  <button
                                    onClick={handleIncomeSubClick}
                                    className={`w-[calc(100%-0.5rem)] flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 ${
                                      isIncomeSubActive 
                                        ? 'bg-white/10 text-white' 
                                        : 'text-white/50 hover:bg-white/5 hover:text-white'
                                    }`}
                                    data-testid={`nav-${incomeSub.id}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <IncomeIcon className={`w-3.5 h-3.5 flex-shrink-0 ${isIncomeSubActive ? 'text-gold-400' : ''}`} />
                                      <span className="text-[11px] font-medium text-left">
                                        {incomeSub.label[language]}
                                      </span>
                                    </div>
                                    {incomeSub.hasOpexSubsections && (
                                      <ChevronRight className={`w-2.5 h-2.5 transition-transform ${isOpexExpanded ? 'rotate-90' : ''} ${isIncomeSubActive ? 'text-gold-400' : ''}`} />
                                    )}
                                  </button>
                                  
                                  {/* OPEX Subsections */}
                                  {incomeSub.hasOpexSubsections && isOpexExpanded && (
                                    <div className="ml-3 space-y-0.5 overflow-hidden transition-all duration-200">
                                      {opexSubsections.map((opexSub) => {
                                        const OpexIcon = opexSub.icon;
                                        const isOpexSubActive = activeSection === opexSub.id;
                                        return (
                                          <button
                                            key={opexSub.id}
                                            onClick={() => setActiveSection(opexSub.id)}
                                            className={`w-[calc(100%-0.25rem)] flex items-center px-2.5 py-1.5 rounded-lg transition-all duration-200 ${
                                              isOpexSubActive 
                                                ? 'bg-white/10 text-white' 
                                                : 'text-white/40 hover:bg-white/5 hover:text-white'
                                            }`}
                                            data-testid={`nav-${opexSub.id}`}
                                          >
                                            <div className="flex items-center gap-2">
                                              <OpexIcon className={`w-3 h-3 flex-shrink-0 ${isOpexSubActive ? 'text-gold-400' : ''}`} />
                                              <span className="text-[10px] font-medium text-left">
                                                {opexSub.label[language]}
                                              </span>
                                            </div>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {/* Budget Execution 2026 Subsections (espelho de 2025) */}
              {section.id === 'budget-execution' && isBudgetExecutionExpanded && (
                <div className="ml-4 space-y-1 overflow-hidden transition-all duration-200">
                  {budgetExecutionSubsections.map((sub) => {
                    const SubIcon = sub.icon;
                    const isSubActive = activeSection === sub.id || (sub.hasIncomeStatementSubsections && isIncomeStatement2026Section(activeSection));
                    
                    return (
                      <React.Fragment key={sub.id}>
                        <button
                          onClick={() => {
                            if (sub.hasIncomeStatementSubsections) {
                              if (activeSection === sub.id) {
                                setIsIncomeStatement2026Expanded(!isIncomeStatement2026Expanded);
                              } else {
                                setActiveSection(sub.id);
                                setIsIncomeStatement2026Expanded(true);
                              }
                            } else {
                              setActiveSection(sub.id);
                            }
                          }}
                          className={`w-[calc(100%-0.5rem)] flex items-center justify-between px-4 py-2.5 rounded-lg transition-all duration-200 ${
                            isSubActive 
                              ? 'bg-white/10 text-white' 
                              : 'text-white/60 hover:bg-white/5 hover:text-white'
                          }`}
                          data-testid={`nav-${sub.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <SubIcon className={`w-4 h-4 flex-shrink-0 ${isSubActive ? 'text-gold-400' : ''}`} />
                            <span className="text-xs font-medium text-left">
                              {sub.label[language]}
                            </span>
                          </div>
                          {sub.hasIncomeStatementSubsections && (
                            <ChevronRight className={`w-3 h-3 transition-transform ${isIncomeStatement2026Expanded ? 'rotate-90' : ''} ${isSubActive ? 'text-gold-400' : ''}`} />
                          )}
                        </button>
                        
                        {/* Income Statement 2026 Subsections */}
                        {sub.hasIncomeStatementSubsections && isIncomeStatement2026Expanded && (
                          <div className="ml-4 space-y-1 overflow-hidden transition-all duration-200">
                            {incomeStatement2026Subsections.map((incomeSub) => {
                              const IncomeIcon = incomeSub.icon;
                              const isIncomeSubActive = activeSection === incomeSub.id || (incomeSub.hasOpexSubsections && opex2026Subsections.some(opexSub => opexSub.id === activeSection));
                              
                              const handleIncomeSubClick = () => {
                                if (incomeSub.hasOpexSubsections) {
                                  if (activeSection === incomeSub.id) {
                                    setIsOpex2026Expanded(!isOpex2026Expanded);
                                  } else if (isOpex2026Section(activeSection)) {
                                    setActiveSection(incomeSub.id);
                                    setIsOpex2026Expanded(true);
                                  } else {
                                    setActiveSection(incomeSub.id);
                                    setIsOpex2026Expanded(true);
                                  }
                                } else {
                                  setActiveSection(incomeSub.id);
                                }
                              };

                              return (
                                <React.Fragment key={incomeSub.id}>
                                  <button
                                    onClick={handleIncomeSubClick}
                                    className={`w-[calc(100%-0.5rem)] flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 ${
                                      isIncomeSubActive 
                                        ? 'bg-white/10 text-white' 
                                        : 'text-white/50 hover:bg-white/5 hover:text-white'
                                    }`}
                                    data-testid={`nav-${incomeSub.id}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <IncomeIcon className={`w-3.5 h-3.5 flex-shrink-0 ${isIncomeSubActive ? 'text-gold-400' : ''}`} />
                                      <span className="text-[11px] font-medium text-left">
                                        {incomeSub.label[language]}
                                      </span>
                                    </div>
                                    {incomeSub.hasOpexSubsections && (
                                      <ChevronRight className={`w-3 h-3 transition-transform ${isOpex2026Expanded ? 'rotate-90' : ''} ${isIncomeSubActive ? 'text-gold-400' : ''}`} />
                                    )}
                                  </button>
                                  
                                  {/* OPEX 2026 Subsections */}
                                  {incomeSub.hasOpexSubsections && isOpex2026Expanded && (
                                    <div className="ml-3 space-y-0.5 overflow-hidden transition-all duration-200">
                                      {opex2026Subsections.map((opexSub) => {
                                        const OpexIcon = opexSub.icon;
                                        const isOpexActive = activeSection === opexSub.id;
                                        
                                        return (
                                          <button
                                            key={opexSub.id}
                                            onClick={() => setActiveSection(opexSub.id)}
                                            className={`w-[calc(100%-0.25rem)] flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-all duration-200 ${
                                              isOpexActive 
                                                ? 'bg-white/10 text-white' 
                                                : 'text-white/40 hover:bg-white/5 hover:text-white'
                                            }`}
                                            data-testid={`nav-${opexSub.id}`}
                                          >
                                            <OpexIcon className={`w-3 h-3 flex-shrink-0 ${isOpexActive ? 'text-gold-400' : ''}`} />
                                            <span className="text-[10px] font-medium text-left">
                                              {opexSub.label[language]}
                                            </span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {/* Reports Subsections */}
              {section.hasReportsSubsections && isReportsExpanded && (
                <div className="ml-4 space-y-1 overflow-hidden transition-all duration-200">
                  {reportsSubsections.map((sub) => {
                    const SubIcon = sub.icon;
                    const isSubActive = activeSection === sub.id;
                    
                    return (
                      <button
                        key={sub.id}
                        onClick={() => setActiveSection(sub.id)}
                        className={`w-[calc(100%-0.5rem)] flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                          isSubActive 
                            ? 'bg-white/10 text-white' 
                            : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }`}
                        data-testid={`nav-${sub.id}`}
                      >
                        <SubIcon className={`w-4 h-4 flex-shrink-0 ${isSubActive ? 'text-gold-400' : ''}`} />
                        <span className="text-xs font-medium text-left">
                          {sub.label[language]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// Metric Card
const MetricCard = ({ title, value, unit, change, changeUnit = '%', isPositive, icon: Icon, color = 'primary', onClick, clickable = false, subtitle }) => {
  // Format value with decimal if it's in millions (M USD, M, etc.)
  const displayValue = (unit && (unit.includes('M') || unit === 'M')) 
    ? formatM(value) 
    : value;
  
  return (
    <div 
      className={`bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300 ${clickable ? 'cursor-pointer hover:border-gold-300' : ''}`}
      onClick={onClick}
      data-testid={`metric-card-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
          color === 'gold' ? 'bg-gold-50' : color === 'emerald' ? 'bg-emerald-50' : 'bg-slate-100'
        }`}>
          <Icon className={`w-5 h-5 ${
            color === 'gold' ? 'text-gold-600' : color === 'emerald' ? 'text-emerald-600' : 'text-primary'
          }`} />
        </div>
        <div className="flex items-center gap-2">
          {change !== undefined && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
              isPositive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
            }`}>
              {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {parseFloat(change) > 0 ? '+' : ''}{change}{changeUnit}
            </div>
          )}
          {clickable && <ChevronRight className="w-4 h-4 text-slate-400" />}
        </div>
      </div>
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{title}</p>
      <p className="font-serif text-3xl font-semibold text-primary">
        {displayValue}<span className="text-base text-slate-400 ml-1 font-sans">{unit}</span>
      </p>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
  );
};

// Executive Summary Highlight Card
const HighlightCard = ({ icon: Icon, value, label, description, isPositive, iconBg }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconBg}`}>
        <Icon className="w-5 h-5" />
      </div>
      <span className={`font-mono text-2xl font-bold ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
        {value}
      </span>
    </div>
    <h4 className="font-semibold text-primary mb-2">{label}</h4>
    <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
  </div>
);

// Strategic Priority Card - McKinsey style with concentric circles icon
const PriorityCard = ({ title, description }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
        <div className="w-6 h-6 rounded-full border-2 border-indigo-600 flex items-center justify-center">
          <div className="w-3 h-3 rounded-full border-2 border-indigo-600"></div>
        </div>
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-lg text-primary mb-2">{title}</h4>
        <p className="text-slate-500 leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

// Insights Section Component - reusable dark theme section
const InsightsSection = ({ title, insights, language }) => (
  <div className="bg-[#1a1f3c] rounded-2xl p-8 mt-8">
    <h3 className="text-white font-semibold text-lg uppercase tracking-wider mb-6">
      {title}
    </h3>
    <div className="grid grid-cols-3 gap-6">
      {insights.map((insight, idx) => (
        <div key={idx} className="bg-slate-700/30 rounded-xl p-6 border border-slate-600/30">
          <h4 className={`font-semibold text-sm uppercase tracking-wider mb-3 ${insight.color || 'text-white'}`}>
            {insight.title}
          </h4>
          <p className="text-slate-300 text-sm leading-relaxed">
            {insight.description}
          </p>
        </div>
      ))}
    </div>
  </div>
);

export const FinancePage = ({ year }) => {
  const { language, toggleLanguage } = useLanguage();
  const { isPresentationMode, registerNavigationCallbacks, unregisterNavigationCallbacks } = usePresentationMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSectionState] = useState('budget-execution');
  const [compareYear, setCompareYear] = useState(year > 2024 ? year - 1 : null);

  // Wrapper function that scrolls to top when changing section
  const setActiveSection = (newSection) => {
    setActiveSectionState(newSection);
    // Scroll to top when changing section
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  };

  // List of navigable sections in order for presentation mode
  const navigableSections = [
    'budget-execution',
    'revenue-detail-2026',
    'ebitda-detail-2026',
    'opex-detail-2026',
    'netincome-detail-2026',
    'liquidity-2026',
    'debt-2026',
    'balance-sheet-2026',
    'capex-2026',
    'multi-period-comparative',
    'performance',
    'revenue-detail',
    'ebitda-detail',
    'opex-detail',
    'netincome-detail',
    'liquidity',
    'debt',
    'balance-sheet',
    'capex',
  ];

  // Register navigation callbacks for presentation mode
  useEffect(() => {
    const goToNextSection = () => {
      const currentIndex = navigableSections.indexOf(activeSection);
      if (currentIndex >= 0 && currentIndex < navigableSections.length - 1) {
        setActiveSection(navigableSections[currentIndex + 1]);
      } else if (currentIndex === -1) {
        // If current section not in list, go to first
        setActiveSection(navigableSections[0]);
      }
    };

    const goToPreviousSection = () => {
      const currentIndex = navigableSections.indexOf(activeSection);
      if (currentIndex > 0) {
        setActiveSection(navigableSections[currentIndex - 1]);
      } else if (currentIndex === -1) {
        // If current section not in list, go to last
        setActiveSection(navigableSections[navigableSections.length - 1]);
      }
    };

    if (isPresentationMode) {
      registerNavigationCallbacks(goToNextSection, goToPreviousSection);
    }

    return () => {
      unregisterNavigationCallbacks();
    };
  }, [isPresentationMode, activeSection, registerNavigationCallbacks, unregisterNavigationCallbacks]);

  // Helper function to get the current section title for breadcrumb
  const getSectionTitle = () => {
    const sectionLabels = {
      'performance': { en: 'Financial Review 2025', pt: 'Revisão Financeira 2025' },
      'budget-execution': { en: 'Budget Execution 2026', pt: 'Execução Orçamental 2026' },
      'reports': { en: 'Reports & Documentation', pt: 'Relatórios e Documentação' },
      'income-statement': { en: 'Income Statement', pt: 'Demonstração de Resultados' },
      'liquidity': { en: 'Liquidity & Cash Generation', pt: 'Liquidez e Geração de Caixa' },
      'debt': { en: 'Debt & Financing', pt: 'Dívida e Financiamento' },
      'capex': { en: 'CAPEX', pt: 'CAPEX' },
      'balance-sheet': { en: 'Balance Sheet', pt: 'Balanço Patrimonial' },
      'revenue-detail': { en: 'Revenue', pt: 'Receitas' },
      'opex-detail': { en: 'Operating Costs', pt: 'Custos Operacionais' },
      'ebitda-detail': { en: 'EBITDA', pt: 'EBITDA' },
      'non-operating': { en: 'Non-Operating Results', pt: 'Resultados Não Operacionais' },
      'financial-results': { en: 'Financial Results', pt: 'Resultados Financeiros' },
      'industrial-tax': { en: 'Industrial Tax', pt: 'Imposto Industrial' },
      'netincome-detail': { en: 'Net Income', pt: 'Resultado Líquido' },
      'costpercarat-detail': { en: 'Unit Cost per Carat', pt: 'Custo Unitário por Quilate' },
      'opex-cogs': { en: 'COGS', pt: 'CMVMC' },
      'opex-personnel': { en: 'Personnel Costs', pt: 'Custos com Pessoal' },
      'opex-subcontracts': { en: 'Subcontracts', pt: 'Subcontratos' },
      'opex-fse': { en: 'General & Administrative', pt: 'Gerais e Administrativos' },
      'opex-commercial': { en: 'Commercial Costs', pt: 'Custos de Comercialização' },
      'external-reports': { en: 'Auditor Report', pt: 'Relatório do Auditor' },
      'internal-docs': { en: 'Financial Reports', pt: 'Relatórios Financeiros' },
      'platform-reports': { en: 'Platform Generated Reports', pt: 'Relatórios Gerados pela Plataforma' },
      'multi-period-comparative': { en: 'Multi-Period Comparison', pt: 'Comparativo Multi-Período' },
      // 2026 sections
      'income-statement-2026': { en: 'Income Statement 2026', pt: 'Demonstração de Resultados 2026' },
      'liquidity-2026': { en: 'Liquidity & Cash Generation 2026', pt: 'Liquidez e Geração de Caixa 2026' },
      'debt-2026': { en: 'Debt & Financing 2026', pt: 'Dívida e Financiamento 2026' },
      'capex-2026': { en: 'CAPEX 2026', pt: 'CAPEX 2026' },
      'balance-sheet-2026': { en: 'Balance Sheet 2026', pt: 'Balanço Patrimonial 2026' },
      'revenue-detail-2026': { en: 'Revenue 2026', pt: 'Receitas 2026' },
      'opex-detail-2026': { en: 'Operating Costs 2026', pt: 'Custos Operacionais 2026' },
      'ebitda-detail-2026': { en: 'EBITDA 2026', pt: 'EBITDA 2026' },
      'non-operating-2026': { en: 'Non-Operating Results 2026', pt: 'Resultados Não Operacionais 2026' },
      'financial-results-2026': { en: 'Financial Results 2026', pt: 'Resultados Financeiros 2026' },
      'industrial-tax-2026': { en: 'Industrial Tax 2026', pt: 'Imposto Industrial 2026' },
      'netincome-detail-2026': { en: 'Net Income 2026', pt: 'Resultado Líquido 2026' },
      'costpercarat-detail-2026': { en: 'Unit Cost per Carat 2026', pt: 'Custo Unitário por Quilate 2026' },
      'opex-cogs-2026': { en: 'COGS 2026', pt: 'CMVMC 2026' },
      'opex-personnel-2026': { en: 'Personnel Costs 2026', pt: 'Custos com Pessoal 2026' },
      'opex-subcontracts-2026': { en: 'Subcontracts 2026', pt: 'Subcontratos 2026' },
      'opex-fse-2026': { en: 'General & Administrative 2026', pt: 'Gerais e Administrativos 2026' },
      'opex-commercial-2026': { en: 'Commercial Costs 2026', pt: 'Custos de Comercialização 2026' },
    };
    return sectionLabels[activeSection]?.[language] || sectionLabels['performance'][language];
  };
  
  // Read section from URL query parameter
  useEffect(() => {
    const sectionParam = searchParams.get('section');
    if (sectionParam) {
      setActiveSection(sectionParam);
    }
  }, [searchParams]);
  
  // Chatbot state - lifted to parent level to persist across section changes
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatbotSession, setChatbotSession] = useState(null);
  
  // Close chatbot when leaving reports section
  React.useEffect(() => {
    if (activeSection !== 'reports' && showChatbot) {
      setShowChatbot(false);
    }
  }, [activeSection, showChatbot]);

  const financeData = mockData.finance;
  const monthlyData = financeData.monthly[year];
  const yearlyData = financeData.yearly[year];
  const debtByBank = mockData.debtByBank[year];
  const capexByDept = mockData.capexByDepartment[year];
  const topInvestments = mockData.topInvestments[2025];
  const deferredInvestments = mockData.deferredInvestments;

  const compareYearlyData = compareYear ? financeData.yearly[compareYear] : null;
  const prevYearData = financeData.yearly[year - 1] || financeData.yearly[2024];

  const getVarianceColor = (variance) => variance >= 0 ? 'text-emerald-600' : 'text-red-600';

  // Executive Summary Data
  const executiveSummaryData = {
    2024: {
      highlights: [
        { 
          icon: TrendingDown, 
          value: '-5%', 
          label: { en: 'Revenue Decline', pt: 'Queda nas Receitas' },
          description: { en: 'Revenue decreased to USD 245M due to lower diamond prices.', pt: 'Receitas caíram para USD 245M devido à queda nos preços dos diamantes.' },
          isPositive: false,
          iconBg: 'bg-red-100 text-red-600'
        },
        { 
          icon: Factory, 
          value: '-12%', 
          label: { en: 'Production Below Plan', pt: 'Produção Abaixo do Plano' },
          description: { en: 'Production reached 88% of plan, demonstrating operational challenges.', pt: 'Produção atingiu 88% do plano, demonstrando desafios operacionais.' },
          isPositive: false,
          iconBg: 'bg-amber-100 text-amber-600'
        },
        { 
          icon: Gem, 
          value: '+1.1%', 
          label: { en: 'Recovery Rate Improvement', pt: 'Melhoria na Taxa de Recuperação' },
          description: { en: 'Recovery rate improved to 92.1%, exceeding target by 1.1%.', pt: 'Taxa de recuperação melhorou para 92.1%, superando meta em 1.1%.' },
          isPositive: true,
          iconBg: 'bg-blue-100 text-blue-600'
        },
        { 
          icon: DollarSign, 
          value: '42M', 
          label: { en: 'Cash Position', pt: 'Posição de Caixa' },
          description: { en: 'Cash position at USD 42M, demonstrating solid financial management.', pt: 'Caixa em USD 42M, demonstrando gestão financeira sólida.' },
          isPositive: true,
          iconBg: 'bg-purple-100 text-purple-600'
        },
      ],
      overview: {
        en: `The year 2024 presented significant challenges for ABC, marked by a substantial decline in revenues and operating profit, primarily due to strong price reductions in the global diamond market. Despite the adverse scenario, the company demonstrated operational resilience with improvement in recovery rates.\n\nThe 5% decline in revenue (to USD 245M) was the main factor impacting financial results. The EBITDA margin remained stable at 31.8%, reflecting cost discipline.\n\nOn the other hand, operational results showed room for improvement. Production reached 88% of plan, and CAPEX execution was at 76%, indicating execution challenges that need to be addressed in 2025.\n\nThe company's cash position remained solid at USD 42M, with Net Debt/EBITDA at 0.69x, demonstrating prudent financial management even in challenging market conditions.`,
        pt: `O ano de 2024 apresentou desafios significativos para a ABC, marcado por uma queda substancial nas receitas e no lucro operacional, principalmente devido à forte redução nos preços dos diamantes no mercado global. Apesar do cenário adverso, a empresa demonstrou resiliência operacional com melhoria nas taxas de recuperação.\n\nA queda de 5% na receita (para USD 245M) foi o principal fator que impactou os resultados financeiros. A margem EBITDA manteve-se estável em 31.8%, refletindo disciplina de custos.\n\nPor outro lado, os resultados operacionais mostraram espaço para melhoria. A produção atingiu 88% do plano, e a execução de CAPEX ficou em 76%, indicando desafios de execução que precisam ser endereçados em 2025.\n\nA posição de caixa da empresa permaneceu sólida em USD 42M, com Dívida Líquida/EBITDA em 0.69x, demonstrando gestão financeira prudente mesmo em condições de mercado desafiadoras.`
      },
      priorities: [
        { title: { en: 'Operational Recovery', pt: 'Recuperação Operacional' }, description: { en: 'Improve production execution to reach planned targets.', pt: 'Melhorar execução da produção para atingir metas planejadas.' } },
        { title: { en: 'CAPEX Acceleration', pt: 'Aceleração do CAPEX' }, description: { en: 'Accelerate strategic investment execution.', pt: 'Acelerar execução de investimentos estratégicos.' } },
        { title: { en: 'Cost Optimization', pt: 'Otimização de Custos' }, description: { en: 'Implement operational efficiencies to protect margins.', pt: 'Implementar eficiências operacionais para proteger margens.' } },
        { title: { en: 'Market Risk Mitigation', pt: 'Mitigação de Riscos de Mercado' }, description: { en: 'Develop strategies for price volatility.', pt: 'Desenvolver estratégias para volatilidade de preços.' } },
      ],
      risks: [
        { en: 'Continued volatility in diamond prices', pt: 'Volatilidade contínua nos preços dos diamantes' },
        { en: 'Uncertainty in global luxury market', pt: 'Incerteza no mercado global de luxo' },
        { en: 'Operational cost pressure', pt: 'Pressão de custos operacionais' },
        { en: 'Supply chain challenges', pt: 'Desafios na cadeia de abastecimento' },
      ],
      opportunities: [
        { en: 'Continued improvement in recovery rates', pt: 'Melhoria contínua nas taxas de recuperação' },
        { en: 'Operational efficiencies through technology', pt: 'Eficiências operacionais através de tecnologia' },
        { en: 'Mineral reserves expansion', pt: 'Expansão de reservas minerais' },
        { en: 'Expected market recovery in 2025', pt: 'Recuperação esperada do mercado em 2025' },
      ],
    },
    2025: {
      highlights: [
        { 
          icon: TrendingUp, 
          value: '+21.6%', 
          label: { en: 'Revenue Growth', pt: 'Crescimento nas Receitas' },
          description: { en: 'Revenue increased from USD 245M to USD 298M.', pt: 'Receitas aumentaram de USD 245M para USD 298M.' },
          isPositive: true,
          iconBg: 'bg-emerald-100 text-emerald-600'
        },
        { 
          icon: Factory, 
          value: '+6pp', 
          label: { en: 'Production Improvement', pt: 'Melhoria na Produção' },
          description: { en: 'Production vs plan improved from 88% to 94%.', pt: 'Produção vs plano melhorou de 88% para 94%.' },
          isPositive: true,
          iconBg: 'bg-blue-100 text-blue-600'
        },
        { 
          icon: Gem, 
          value: '+30.8%', 
          label: { en: 'EBITDA Growth', pt: 'Crescimento do EBITDA' },
          description: { en: 'EBITDA increased from USD 78M to USD 102M with margin improvement.', pt: 'EBITDA aumentou de USD 78M para USD 102M com melhoria de margem.' },
          isPositive: true,
          iconBg: 'bg-gold-100 text-gold-600'
        },
        { 
          icon: DollarSign, 
          value: '56M', 
          label: { en: 'Cash Position', pt: 'Posição de Caixa' },
          description: { en: 'Cash increased to USD 56M, +33% vs prior year.', pt: 'Caixa aumentou para USD 56M, +33% vs ano anterior.' },
          isPositive: true,
          iconBg: 'bg-purple-100 text-purple-600'
        },
      ],
      overview: {
        en: `2025 marked a strong recovery year for ABC, with significant improvements across all key metrics. Revenue grew 21.6% year-over-year, driven by improved diamond prices and higher production volumes.\n\nThe EBITDA margin expanded from 31.8% to 34.2%, reflecting operational leverage and cost discipline. Production execution improved substantially, reaching 94% of plan compared to 88% in the prior year.\n\nCash generation was robust, with Free Cash Flow of USD 45M, enabling debt reduction and strengthening the balance sheet. Net Debt/EBITDA improved dramatically from 0.69x to 0.35x.\n\nThe company successfully executed 87% of planned CAPEX, with key investments in processing capacity and fleet renewal completed on schedule.`,
        pt: `2025 marcou um ano de forte recuperação para a ABC, com melhorias significativas em todas as métricas-chave. A receita cresceu 21.6% ano-a-ano, impulsionada pela melhoria nos preços dos diamantes e maiores volumes de produção.\n\nA margem EBITDA expandiu de 31.8% para 34.2%, refletindo alavancagem operacional e disciplina de custos. A execução da produção melhorou substancialmente, atingindo 94% do plano comparado com 88% no ano anterior.\n\nA geração de caixa foi robusta, com Fluxo de Caixa Livre de USD 45M, permitindo redução da dívida e fortalecimento do balanço. Dívida Líquida/EBITDA melhorou dramaticamente de 0.69x para 0.35x.\n\nA empresa executou com sucesso 87% do CAPEX planejado, com investimentos-chave em capacidade de processamento e renovação de frota concluídos dentro do prazo.`
      },
      priorities: [
        { title: { en: 'Capacity Expansion', pt: 'Expansão de Capacidade' }, description: { en: 'Complete Phase 2 expansion to increase production.', pt: 'Concluir expansão Fase 2 para aumentar produção.' } },
        { title: { en: 'Margin Protection', pt: 'Proteção de Margem' }, description: { en: 'Maintain cost discipline despite growth.', pt: 'Manter disciplina de custos apesar do crescimento.' } },
        { title: { en: 'Talent Development', pt: 'Desenvolvimento de Talento' }, description: { en: 'Invest in technical capabilities and retention.', pt: 'Investir em capacidades técnicas e retenção.' } },
        { title: { en: 'ESG Excellence', pt: 'Excelência ESG' }, description: { en: 'Maintain industry-leading safety and environmental standards.', pt: 'Manter padrões de segurança e ambiente líderes na indústria.' } },
      ],
      risks: [
        { en: 'Market price volatility', pt: 'Volatilidade de preços de mercado' },
        { en: 'Execution risk on expansion projects', pt: 'Risco de execução em projetos de expansão' },
        { en: 'Talent retention in critical functions', pt: 'Retenção de talento em funções críticas' },
        { en: 'Regulatory compliance requirements', pt: 'Requisitos de compliance regulatório' },
      ],
      opportunities: [
        { en: 'Growing demand in emerging markets', pt: 'Procura crescente em mercados emergentes' },
        { en: 'Premium pricing for high-quality stones', pt: 'Preços premium para pedras de alta qualidade' },
        { en: 'Technology-driven efficiency gains', pt: 'Ganhos de eficiência através de tecnologia' },
        { en: 'Strategic partnerships', pt: 'Parcerias estratégicas' },
      ],
    },
    2026: {
      highlights: [
        { 
          icon: TrendingUp, 
          value: '+4.7%', 
          label: { en: 'Revenue Growth', pt: 'Crescimento nas Receitas' },
          description: { en: 'Revenue reached USD 250M, growing steadily despite challenges.', pt: 'Receita atingiu USD 250M, crescendo de forma estável apesar dos desafios.' },
          isPositive: true,
          iconBg: 'bg-emerald-100 text-emerald-600'
        },
        { 
          icon: Factory, 
          value: '-2pp', 
          label: { en: 'Production Pressure', pt: 'Pressão na Produção' },
          description: { en: 'Production at 92% of plan, slight decline from prior year.', pt: 'Produção em 92% do plano, ligeira queda vs ano anterior.' },
          isPositive: false,
          iconBg: 'bg-amber-100 text-amber-600'
        },
        { 
          icon: Gem, 
          value: '+15.7%', 
          label: { en: 'EBITDA Growth', pt: 'Crescimento do EBITDA' },
          description: { en: 'EBITDA grew to USD 118M with margin expansion to 37.8%.', pt: 'EBITDA cresceu para USD 118M com expansão de margem para 37.8%.' },
          isPositive: true,
          iconBg: 'bg-gold-100 text-gold-600'
        },
        { 
          icon: DollarSign, 
          value: '48M', 
          label: { en: 'Cash Position', pt: 'Posição de Caixa' },
          description: { en: 'Cash at USD 48M with Net Debt/EBITDA at historic low of 0.20x.', pt: 'Caixa em USD 48M com Dívida Líquida/EBITDA no mínimo histórico de 0.20x.' },
          isPositive: true,
          iconBg: 'bg-purple-100 text-purple-600'
        },
      ],
      overview: {
        en: `2026 (YTD) shows continued financial strength despite operational headwinds. Revenue grew 4.7% to USD 250M, while EBITDA reached USD 118M with an industry-leading margin of 37.8%.\n\nProduction execution faces some pressure at 92% of plan, primarily due to ore processing challenges. However, recovery rates continue to improve, reaching 94.2%, the highest in company history.\n\nThe balance sheet remains exceptionally strong with Net Debt/EBITDA at 0.20x, providing significant financial flexibility. CAPEX execution at 82% reflects timing decisions rather than project cancellations, with several investments deferred to optimize capital allocation.\n\nTurnover in technical functions has increased to 7.5%, requiring focused retention initiatives.`,
        pt: `2026 (YTD) mostra força financeira contínua apesar de desafios operacionais. A receita cresceu 4.7% para USD 250M, enquanto o EBITDA atingiu USD 118M com margem líder na indústria de 37.8%.\n\nA execução da produção enfrenta alguma pressão em 92% do plano, principalmente devido a desafios no processamento de minério. No entanto, as taxas de recuperação continuam a melhorar, atingindo 94.2%, o mais alto na história da empresa.\n\nO balanço permanece excepcionalmente forte com Dívida Líquida/EBITDA em 0.20x, proporcionando flexibilidade financeira significativa. A execução de CAPEX em 82% reflete decisões de timing em vez de cancelamentos de projetos, com vários investimentos diferidos para otimizar alocação de capital.\n\nA rotatividade em funções técnicas aumentou para 7.5%, exigindo iniciativas focadas de retenção.`
      },
      priorities: [
        { title: { en: 'Ore Processing Optimization', pt: 'Otimização do Processamento' }, description: { en: 'Address bottlenecks in ore processing operations.', pt: 'Resolver gargalos nas operações de processamento.' } },
        { title: { en: 'Talent Retention', pt: 'Retenção de Talento' }, description: { en: 'Implement targeted retention programs for technical staff.', pt: 'Implementar programas de retenção direcionados para staff técnico.' } },
        { title: { en: 'CAPEX Catch-up', pt: 'Recuperação do CAPEX' }, description: { en: 'Accelerate deferred investment execution in H2.', pt: 'Acelerar execução de investimentos diferidos no H2.' } },
        { title: { en: 'Cash Deployment', pt: 'Aplicação de Caixa' }, description: { en: 'Evaluate optimal use of strong cash position.', pt: 'Avaliar uso ótimo da forte posição de caixa.' } },
      ],
      risks: [
        { en: 'Ore processing constraints', pt: 'Restrições no processamento de minério' },
        { en: 'Technical talent turnover', pt: 'Rotatividade de talento técnico' },
        { en: 'CAPEX timing risks', pt: 'Riscos de timing do CAPEX' },
        { en: 'Market uncertainty', pt: 'Incerteza de mercado' },
      ],
      opportunities: [
        { en: 'Strong balance sheet flexibility', pt: 'Flexibilidade de balanço forte' },
        { en: 'Record high recovery rates', pt: 'Taxas de recuperação em máximo histórico' },
        { en: 'Margin expansion potential', pt: 'Potencial de expansão de margem' },
        { en: 'Strategic investment capacity', pt: 'Capacidade de investimento estratégico' },
      ],
    },
  };

  const summaryData = executiveSummaryData[year];

  // Helper function to get the Finance FlowMap component with proper props
  const getFinanceFlowMap = (yearOverride = null) => {
    // Check if we're in a 2026 section (including budget-execution which is the 2026 main page)
    const is2026Section = activeSection?.includes('-2026') || activeSection?.includes('-26') || activeSection === 'budget-execution';
    const flowMapYear = yearOverride || (is2026Section ? 2026 : 2025);
    return (
      <FinanceFlowMap
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        year={flowMapYear}
        language={language}
      />
    );
  };

  // Content based on active section
  const renderContent = () => {
    // Dados para 2026
    const yearlyData2026 = mockData.finance.yearly[2026];
    const prevYearData2026 = mockData.finance.yearly[2025];
    
    // Get flowMap component for pages that need it (exclude reports/audit)
    const flowMap = !activeSection?.includes('reports') && !activeSection?.includes('audit') && !activeSection?.includes('internal-docs') && !activeSection?.includes('external') && !activeSection?.includes('platform')
      ? getFinanceFlowMap() 
      : null;

    switch (activeSection) {
      case 'budget-execution':
        return (
          <FinancialExecutiveSummary
            year={2026}
            flowMap={flowMap}
            setActiveSection={setActiveSection}
          />
        );

      // ============ SECÇÕES 2026 (ESPELHO DE 2025) ============
      case 'income-statement-2026':
        return (
          <IncomeStatementMain
            year={2026}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'revenue-detail-2026':
        return (
          <RevenueAnalysis
            year={2026}
            language={language}
            yearlyData={yearlyData2026}
            prevYearData={prevYearData2026}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'ebitda-detail-2026':
        return (
          <EBITDADetail
            year={2026}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'opex-detail-2026':
        return (
          <OPEXDetail
            year={2026}
            language={language}
            yearlyData={yearlyData2026}
            prevYearData={prevYearData2026}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'opex-cogs-2026':
        return (
          <COGSAnalysis 
            year={2026} 
            language={language} 
            onBack={() => setActiveSection('opex-detail-2026')}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'opex-personnel-2026':
        return (
          <PersonnelCosts 
            year={2026} 
            language={language} 
            onBack={() => setActiveSection('opex-detail-2026')}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'opex-subcontracts-2026':
        return (
          <SubcontractsAnalysis 
            year={2026} 
            language={language} 
            onBack={() => setActiveSection('opex-detail-2026')}
            flowMap={flowMap}
          />
        );

      case 'opex-fse-2026':
        return (
          <GeneralAdminAnalysis 
            year={2026} 
            language={language} 
            onBack={() => setActiveSection('opex-detail-2026')}
            flowMap={flowMap}
          />
        );

      case 'opex-commercial-2026':
        return (
          <CommercialCostsAnalysis 
            year={2026} 
            language={language} 
            onBack={() => setActiveSection('opex-detail-2026')}
            flowMap={flowMap}
          />
        );

      case 'non-operating-2026':
        return (
          <NonOperatingResults
            year={2026}
            language={language}
            yearlyData={yearlyData2026}
            prevYearData={prevYearData2026}
            flowMap={flowMap}
          />
        );

      case 'financial-results-2026':
        return (
          <FinancialResults
            year={2026}
            language={language}
            yearlyData={yearlyData2026}
            prevYearData={prevYearData2026}
            flowMap={flowMap}
          />
        );

      case 'industrial-tax-2026':
        return (
          <IndustrialTax
            year={2026}
            language={language}
            flowMap={flowMap}
          />
        );

      case 'netincome-detail-2026':
        return (
          <NetIncomeDetail
            year={2026}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'costpercarat-detail-2026':
        return (
          <CostPerCaratDetail
            year={2026}
            language={language}
            flowMap={flowMap}
          />
        );

      case 'liquidity-2026':
        return (
          <LiquidityCashFlow
            year={2026}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'balance-sheet-2026':
        return (
          <BalanceSheet
            year={2026}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'debt-2026':
        return (
          <DebtFinancing
            year={2026}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'capex-2026':
        return (
          <CapexInvestment
            year={2026}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      // ============ SECÇÕES 2025 ============
      case 'performance':
        return (
          <FinancialExecutiveSummary
            year={2025}
            flowMap={flowMap}
            setActiveSection={setActiveSection}
          />
        );

      case 'revenue-detail':
        return (
          <RevenueAnalysis
            year={2025}
            language={language}
            yearlyData={financeData.yearly[2025]}
            prevYearData={financeData.yearly[2024]}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'ebitda-detail':
        return (
          <EBITDADetail
            year={2025}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'opex-detail':
        return (
          <OPEXDetail
            year={2025}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'opex-personnel':
        return (
          <PersonnelCosts
            year={2025}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );


      case 'opex-cogs':
        return (
          <COGSAnalysis 
            year={2025} 
            language={language} 
            onBack={() => setActiveSection('opex-detail')}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'opex-fse':
        return (
          <GeneralAdminAnalysis 
            language={language}
            year={2025}
            onBack={() => setActiveSection('opex-detail')}
            flowMap={flowMap}
          />
        );

      case 'opex-subcontracts':
        return (
          <SubcontractsAnalysis 
            language={language}
            year={2025}
            onBack={() => setActiveSection('opex-detail')}
            flowMap={flowMap}
          />
        );

      case 'opex-commercial':
        return (
          <CommercialCostsAnalysis 
            language={language}
            year={2025}
            onBack={() => setActiveSection('opex-detail')}
            flowMap={flowMap}
          />
        );

      case 'netincome-detail':
        return (
          <NetIncomeDetail
            year={2025}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );


      case 'non-operating':
        return (
          <NonOperatingResults
            year={2025}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );


      case 'financial-results':
        return (
          <FinancialResults
            year={2025}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );


      case 'industrial-tax':
        return (
          <IndustrialTax
            year={2025}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );


      case 'costpercarat-detail':
        return (
          <CostPerCaratDetail
            year={2025}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );


      case 'liquidity':
        return (
          <LiquidityCashFlow
            year={2025}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'balance-sheet':
        return (
          <BalanceSheet
            year={2025}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'debt':
        return (
          <DebtFinancing
            year={2025}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'capex':
        return (
          <CapexInvestment
            year={2025}
            language={language}
            setActiveSection={setActiveSection}
            flowMap={flowMap}
          />
        );

      case 'social-responsibility':
        return (
          <SocialResponsibility
            year={2025}
            language={language}
            flowMap={flowMap}
          />
        );

      case 'income-statement':
        // Income Statement - Demonstração de Resultados (Página-Mãe)
        return <IncomeStatementMain year={2025} language={language} />;

      case 'external-reports':
        // Auditor Report - using ExternalAudit component
        return (
          <ExternalAudit
            year={2025}
            language={language}
          />
        );

      case 'internal-docs':
        // Internal Documentation - Documentação Interna
        const internalDocsArchive = {
          financialStatements: {
            title: { en: 'Financial Statements', pt: 'Demonstrações Financeiras' },
            description: { en: 'Annual and interim financial statements', pt: 'Demonstrações financeiras anuais e intercalares' },
            icon: FileSpreadsheet,
            documents: [
              { name: { en: 'Financial Statements 2025', pt: 'Demonstrações Financeiras 2025' }, date: '2025-12-31', year: '2025', status: 'draft' },
              { name: { en: 'Financial Statements 2024', pt: 'Demonstrações Financeiras 2024' }, date: '2024-12-31', year: '2024', status: 'final' },
            ]
          },
          managementReport: {
            title: { en: 'Management Report', pt: 'Relatório de Gestão' },
            description: { en: 'Annual management reports and analysis', pt: 'Relatórios de gestão e análise anual' },
            icon: Briefcase,
            documents: [
              { name: { en: 'Management Report 2025', pt: 'Relatório de Gestão 2025' }, date: '2025-12-31', year: '2025', status: 'draft' },
              { name: { en: 'Management Report 2024', pt: 'Relatório de Gestão 2024' }, date: '2024-12-31', year: '2024', status: 'final' },
            ]
          },
          budgetPlan: {
            title: { en: 'Budget & Activity Plan', pt: 'Plano de Orçamento e Atividades' },
            description: { en: 'Annual budget and activity planning', pt: 'Planeamento orçamental e de atividades anual' },
            icon: Calculator,
            documents: [
              { name: { en: 'Budget Plan 2026', pt: 'Plano de Orçamento e Atividades 2026' }, date: '2025-11-30', year: '2026', status: 'approved' },
              { name: { en: 'Budget Plan 2025', pt: 'Plano de Orçamento e Atividades 2025' }, date: '2024-11-30', year: '2025', status: 'final' },
            ]
          }
        };

        const getStatusBadge = (status, lang) => {
          const statusConfig = {
            draft: { label: { en: 'Draft', pt: 'Rascunho' }, bg: 'bg-amber-100', text: 'text-amber-700' },
            final: { label: { en: 'Final', pt: 'Final' }, bg: 'bg-emerald-100', text: 'text-emerald-700' },
            approved: { label: { en: 'Approved', pt: 'Aprovado' }, bg: 'bg-blue-100', text: 'text-blue-700' },
          };
          const config = statusConfig[status] || statusConfig.draft;
          return (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${config.bg} ${config.text}`}>
              {config.label[lang]}
            </span>
          );
        };

        return (
          <div className="space-y-6">
            <div>
              <h2 className="font-serif text-3xl font-bold text-primary">
                {language === 'pt' ? 'Relatórios Financeiros' : 'Financial Reports'}
              </h2>
              <p className="text-slate-500 mt-2">
                {language === 'pt' 
                  ? 'Arquivo de documentos financeiros e de gestão' 
                  : 'Financial and management document archive'}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-6">
              {Object.entries(internalDocsArchive).map(([key, category]) => {
                const IconComponent = category.icon;
                return (
                  <div key={key} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] px-5 py-4">
                      <div className="flex items-center gap-3">
                        <IconComponent className="w-5 h-5 text-gold-400" />
                        <div>
                          <h3 className="font-serif text-base font-bold text-white">
                            {category.title[language]}
                          </h3>
                          <p className="text-slate-400 text-xs mt-0.5">
                            {category.description[language]}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 space-y-2">
                      {category.documents.map((doc, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-slate-400" />
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-slate-700">{doc.name[language]}</p>
                                {getStatusBadge(doc.status, language)}
                              </div>
                              <p className="text-xs text-slate-400">{doc.date}</p>
                            </div>
                          </div>
                          <Download className="w-4 h-4 text-primary" />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'platform-reports':
        return (
          <PlatformReportsSection 
            language={language}
            showChatbot={showChatbot}
            setShowChatbot={setShowChatbot}
            chatbotSession={chatbotSession}
            setChatbotSession={setChatbotSession}
          />
        );

      case 'multi-period-comparative':
        return (
          <FinanceMultiPeriodComparative
            language={language}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen bg-slate-50 flex ${isPresentationMode ? 'presentation-mode' : ''}`} data-testid="finance-page">
      {/* McKinsey-style Sidebar - Hidden in Presentation Mode */}
      {!isPresentationMode && (
        <SidebarNav activeSection={activeSection} setActiveSection={setActiveSection} language={language} />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header - Sticky */}
        <div className={`bg-white border-b border-slate-200 px-8 py-3 sticky top-0 z-40 shadow-sm ${isPresentationMode ? 'bg-primary text-white' : ''}`}>
          <div className="flex items-center justify-between">
            {/* Left side: Navigation + Department + Section */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {!isPresentationMode && (
                <>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => navigate('/')}
                    className="text-slate-600 hover:text-primary flex-shrink-0"
                    data-testid="back-to-dashboard"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    <span className="font-serif text-sm">{language === 'pt' ? 'Cockpit Executivo' : 'Executive Cockpit'}</span>
                  </Button>
                  <div className="h-4 w-px bg-slate-300 flex-shrink-0"></div>
                </>
              )}
              <span className={`font-serif font-bold text-lg flex-shrink-0 ${isPresentationMode ? 'text-white' : 'text-primary'}`}>
                {isPresentationMode ? 'ABC' : (language === 'pt' ? 'Finanças' : 'Finance')}
              </span>
              <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isPresentationMode ? 'text-white/60' : 'text-slate-400'}`} />
              <span className={`font-serif text-base truncate ${isPresentationMode ? 'text-white/90' : 'text-slate-600'}`}>
                {getSectionTitle()}
              </span>
            </div>

            {/* Right side: Presentation Mode Button + Language Toggle */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <PresentationModeButton />
              <button
                onClick={toggleLanguage}
                className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${
                  isPresentationMode 
                    ? 'bg-white/20 hover:bg-white/30 text-white' 
                    : 'bg-primary/10 hover:bg-primary/20 text-primary'
                }`}
                data-testid="finance-language-toggle"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm font-medium uppercase">{language}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area - Lavender background for all Finance sections */}
        <div className={`p-8 flex-1 bg-gradient-to-br from-slate-100 via-indigo-50/50 to-purple-50/30 overflow-auto ${isPresentationMode ? 'p-12' : ''}`}>
          <div 
            key={activeSection} 
            className={isPresentationMode ? 'presentation-fade-in' : ''}
          >
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancePage;
